import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {RouterLink} from '@angular/router';
import {IsGranted} from '../../../../../../decorator/is-granted';
import {MetaTitle} from '../../../../../../decorator/meta-title';
import {Paginator} from '../../../../../../tools/pagination/paginator';
import {TableSorter} from '../../../../../../tools/table-sorter/table-sorter';
import {AbstractWidget} from '../../../../../../models/cms/widget/abstract-widget';
import {LoaderService} from '../../../../../../services/loader/loader.service';
import {SnackbarService} from '../../../../../../services/snackbar/snackbar.service';
import {WidgetService} from '../../../../../../services/cms/widget.service';
import {PaginatorComponent} from '../../../../../layout/paginator/paginator.component';
import {TableSorterHeaderDirective} from '../../../../../../directives/table-sorter/table-sorter-header.directive';
import {TableSorterDirective} from '../../../../../../directives/table-sorter/table-sorter.directive';
import {ConfirmDirective} from '../../../../../../directives/confirm/confirm.directive';
import {IsGrantedDirective} from '../../../../../../directives/is-granted/is-granted.directive';
import {FormsModule} from '@angular/forms';
import {Subject} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {Language} from '../../../../../../models/language';
import {ProductService} from '../../../../../../services/product/product.service';
import {Product} from '../../../../../../models/product';
import {DropdownSelectComponent} from '../../../../../layout/dropdown-select/dropdown-select.component';
import {
  DropdownSelectOptionComponent
} from '../../../../../layout/dropdown-select-option/dropdown-select-option.component';
import {DatePipe} from '@angular/common';
import {LangService} from '../../../../../../services/language/lang.service';
import {ConfirmService} from '../../../../../../services/confirm/confirm.service';
import {AuthService} from '../../../../../../services/auth/auth.service';
import {User} from '../../../../../../models/user';
import {ContentLibraryTypes, WidgetTypes} from '../../../../../../constants/widget-type';
import {UserService} from '../../../../../../services/user/user.service';
import {ListFilters} from '../../../../../../tools/list-filters/list-filters';
import {ListFilterService} from '../../../../../../services/list-filter/list-filter.service';

@Component({
  selector: 'app-widget-list',
  imports: [
    RouterLink,
    PaginatorComponent,
    TableSorterHeaderDirective,
    TableSorterDirective,
    ConfirmDirective,
    IsGrantedDirective,
    FormsModule,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    DatePipe,
  ],
  templateUrl: './widget-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './widget-list.component.scss'
})
@IsGranted(['WIDGET_VIEW'])
@MetaTitle('Widget List')
export class WidgetListComponent implements OnInit {
  connectedUser!: User;

  widgets: AbstractWidget[] = [];
  products: Product[] = [];
  languages: Language[] = [];
  users: User[] = [];
  widgetTypes = WidgetTypes;

  paginator: Paginator = new Paginator();
  tableSorter: TableSorter = new TableSorter('id', 'asc');

  filters: ListFilters;

  private nameChange$ = new Subject<void>();

  constructor(
    protected langService: LangService,
    private auth: AuthService,
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private widgetService: WidgetService,
    private productService: ProductService,
    private userService: UserService,
    private confirm: ConfirmService,
    private listFilterService: ListFilterService,
  ) {
    this.filters = listFilterService.createFilters({
      name: 'string',
      productId: 'number[]',
      lang: 'string[]',
      type: 'string[]',
      userId: 'number[]',
    }, this.paginator, this.tableSorter);
  }

  ngOnInit(): void {
    this.auth.getConnectedUser().subscribe((user: User) => {
      this.connectedUser = user;
    });

    this.productService.findAll({sort: 'name'}).subscribe((products: Product[]) => {
      this.products = products;
    });

    this.langService.getLanguages().subscribe((languages: Language[]) => {
      this.languages = languages;
    });

    this.userService.findAll({sort: 'firstname,lastname'}).subscribe((users: User[]) => {
      this.users = users;
    });

    this.nameChange$.pipe(debounceTime(300)).subscribe(() => this.filters.onFilterChange());

    this.filters.init(() => this.fetchAll());
  }

  onNameChange(): void {
    this.nameChange$.next();
  }

  fetchAll(): void {
    const params: any = {groups: 'site', ...this.filters.toParams()};

    if (!params['type']) {
      params['type'] = WidgetTypes.map((type: any) => type.identifier);
    }

    this.loader.startLoading();
    this.widgetService.findAll(params, this.paginator, this.tableSorter).subscribe((widgets: AbstractWidget[]) => {
      this.widgets = widgets;
      this.loader.stopLoading();
    });
  }

  ownsWidget(widget: AbstractWidget): boolean {
    return widget.user?.id === this.connectedUser?.id;
  }

  delete(widget: AbstractWidget): void {
    if (widget.id) {
      this.loader.startLoading();
      this.widgetService.delete(widget.id).subscribe(() => {
        this.loader.stopLoading();
        this.snackbar.success('Widget has been deleted');
        this.fetchAll();
      });
    }
  }

  duplicate(widget: AbstractWidget): void {
    this.loader.startLoading();
    this.widgetService.findOneById(widget.id!, {groups: 'site,theme'}).subscribe((fullWidget: AbstractWidget) => {
      const params: any = {
        type: fullWidget.type,
        name: fullWidget.name + ' (copy)',
        siteId: fullWidget.site?.id ?? null,
        scope: fullWidget.scope,
        enabled: fullWidget.enabled,
        theme: fullWidget.theme,
      };

      for (const property in fullWidget.options) {
        params[property] = fullWidget.options[property];
      }

      this.widgetService.create(params).subscribe({
        next: () => {
          this.loader.stopLoading();
          this.snackbar.success('Widget duplicated successfully.');
          this.fetchAll();
        },
        error: () => {
          this.loader.stopLoading();
        }
      });
    });
  }

  confirmDelete(widget: AbstractWidget): void {
    this.confirm.open('This is a public widget! Deleting it might break some static pages created by other users. Are you sure about deleting this widget?').subscribe((result: boolean) => {
      if (result) {
        this.delete(widget);
      }
    })
  }

  protected readonly contentLibraryTypes = ContentLibraryTypes;
}
